import { defineStore } from "pinia";
import { useRoute } from "vue-router";
import { db } from "@/firebaseConfig";
import { collection, doc, getDocs, onSnapshot, query, orderBy } from "firebase/firestore";
import { useAuthStore } from "@/stores/authStore";
import { ref, computed, watchEffect, watch } from "vue";
import { debounce } from "lodash";

export const useDbStore = defineStore("db", () => {
  const route = useRoute();
  const authStore = useAuthStore();
  const userUID = computed(() => authStore.user?.uid || null);
  const userActive = computed(() => authStore.active);

  // Reactive mode variable (based on route)
  const mode = ref(null);
  watchEffect(() => {
    if (route.path.startsWith("/public")) {
      mode.value = route.params.mode || "funnel";
    } else {
      mode.value = null;
    }
  });

  // Determine which collection to fetch from
  const collectionPath = computed(() => (mode.value ? "public" : userUID.value));

  // Should we fetch snippets?
  const shouldFetchSnippets = computed(() => {
    return mode.value !== null || (!!userUID.value && userActive.value);
  });

  // Reactive state
  const snippets = ref([]);
  const loading = ref(false);
  const initialFetchDone = ref(false);

  // Actual fetch function
  async function fetchSnippets() {
    if (!shouldFetchSnippets.value || !collectionPath.value) {
      console.log("Skipping fetchSnippets(): Condition not met or collectionPath is invalid.");
      return;
    }
    console.log("Fetching from collection:", collectionPath.value);
    loading.value = true;
    try {
      const docRef = doc(db, "users", collectionPath.value);
      const snippetsRef = collection(docRef, "snippets");
      const q = query(snippetsRef, orderBy("order", "asc"), orderBy("name", "asc"));
      const querySnapshot = await getDocs(q);
      snippets.value = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        owner: userUID.value,
        isPublic: mode.value !== null,
      }));
    } catch (error) {
      console.error("Error fetching snippets:", error);
    } finally {
      loading.value = false;
      initialFetchDone.value = true;
    }
  }

  // Real-time listener (if you choose to use it)
  function listenToSnippets() {
    if (!shouldFetchSnippets.value) return;
    const docRef = doc(db, "users", collectionPath.value);
    const snippetsRef = collection(docRef, "snippets");
    return onSnapshot(snippetsRef, (snapshot) => {
      snippets.value = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
    });
  }

  // ---- Guard to avoid multiple rapid fetches ----
  let lastFetchTime = 0;
  const MIN_FETCH_INTERVAL = 1000; // milliseconds

  async function safeFetchSnippets() {
    const now = Date.now();
    if (now - lastFetchTime < MIN_FETCH_INTERVAL) {
      console.log("Skipping fetch: occurred too recently.");
      return;
    }
    lastFetchTime = now;
    await fetchSnippets();
  }

  // Debounced function for subsequent updates
  const debouncedFetchSnippets = debounce(async () => {
    await safeFetchSnippets();
  }, 1000);

  // ---- Combined watcher for auth and mode changes ----
  // We’re watching a combined state so that we don’t trigger multiple times for nearly simultaneous updates.
  watch(
    () => ({
      uid: userUID.value,
      active: userActive.value,
      mode: mode.value,
    }),
    async (newVal, oldVal) => {
      // Cancel any pending debounced call.
      debouncedFetchSnippets.cancel();

      // First run (oldVal is undefined) – fetch immediately.
      if (!oldVal) {
        await safeFetchSnippets();
        return;
      }

      // If the only change is the auth transition from "not logged in" to "logged in", delay a bit.
      if (!oldVal.uid && newVal.uid) {
        // Wait a short time so that all auth changes settle.
        setTimeout(async () => {
          await safeFetchSnippets();
        }, 1000);
      } else {
        // For other changes (or if the auth hasn't just transitioned), use debounce.
        debouncedFetchSnippets();
      }
    },
    { immediate: true, deep: true }
  );

  return {
    mode,
    collectionPath,
    snippets,
    loading,
    fetchSnippets,
    listenToSnippets
  };
});
